'use client';

import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { createPortal } from 'react-dom';
import {
  AdvancedMarker,
  useAdvancedMarkerRef,
} from '@vis.gl/react-google-maps';
import { useClickOutside } from 'src/hooks/common/useClickOutside';
import { MarkerCircle } from '../MarkerCircle';
import { Button, Checkbox } from 'src/components';
import { useOpenProviderProfile } from 'src/hooks/drawers/useOpenProviderProfile';
import { ProviderPopupDetails } from 'src/components/Maps/ProviderPopupDetails';
import getProviderPopupMetrics from '../getProviderPopupMetrics';

// Define a more specific type for provider details if possible
type ProviderDetails = any; // Replace 'any' with a more specific interface if available

type TreeClusterMarkerProps = {
  clusterId: number;
  onMarkerClick?: (
    marker: google.maps.marker.AdvancedMarkerElement | null,
    clusterId: number
  ) => void;
  position: google.maps.LatLngLiteral;
  size: number;
  sizeAsText: string;
  isHovered?: boolean;
  onMouseOver?: (clusterId: number) => void;
  onMouseOut?: () => void;
  clusterLeaves?: any[];
  addToList?: (details: any) => void;
};

const FeaturesClusterMarker: React.FC<TreeClusterMarkerProps> = ({
  position,
  size,
  sizeAsText,
  isHovered = false,
  onMarkerClick,
  onMouseOver,
  onMouseOut,
  clusterId,
  clusterLeaves = [],
  addToList,
}) => {
  const openProviderProfile = useOpenProviderProfile();
  const [markerRef, marker] = useAdvancedMarkerRef();
  const popoverRef = useRef<HTMLDivElement>(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [selectedProviders, setSelectedProviders] = useState<ProviderDetails[]>(
    []
  ); // Use array of objects
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Clicking (or pressing Enter on the focused marker) opens the popover too, for
  // touch and keyboard users who have no hover. The parent still gets the click, so
  // its zoom behavior is unchanged.
  const handleMarkerClick = useCallback(() => {
    if (clusterLeaves.length > 0) setIsPopoverOpen(true);
    onMarkerClick?.(marker, clusterId);
  }, [clusterLeaves.length, onMarkerClick, marker, clusterId]);

  const updatePopoverPosition = useCallback(() => {
    const markerNode = marker?.element as HTMLElement | null;
    const popoverNode = popoverRef.current;

    if (markerNode && popoverNode) {
      const markerRect = markerNode.getBoundingClientRect();
      const popoverHeight = popoverNode.offsetHeight;
      const popoverWidth = popoverNode.offsetWidth;

      const top = markerRect.top + window.scrollY - popoverHeight - 8;
      const left =
        markerRect.left +
        window.scrollX +
        markerRect.width / 2 -
        popoverWidth / 2;

      setPopoverPosition({ top, left });
    }
  }, [marker]);

  useEffect(() => {
    if (isPopoverOpen) {
      requestAnimationFrame(() => {
        updatePopoverPosition();
      });

      window.addEventListener('scroll', updatePopoverPosition, {
        capture: true,
        passive: true,
      });
      window.addEventListener('resize', updatePopoverPosition, {
        passive: true,
      });

      return () => {
        window.removeEventListener('scroll', updatePopoverPosition, {
          capture: true,
          passive: true,
        } as any);
        window.removeEventListener('resize', updatePopoverPosition, {
          passive: true,
        } as any);
      };
    } else {
      setPopoverPosition(null);
    }
  }, [isPopoverOpen, updatePopoverPosition]);

  const leaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = () => {
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
    onMouseOver?.(clusterId);
    if (clusterLeaves.length > 0) {
      setIsPopoverOpen(true);
    }
  };

  const handleMouseLeave = () => {
    leaveTimerRef.current = setTimeout(() => {
      if (!popoverRef.current?.matches(':hover')) {
        setIsPopoverOpen(false);
        onMouseOut?.();
      }
      leaveTimerRef.current = null;
    }, 100);
  };

  const handlePopoverMouseLeave = (e: React.MouseEvent) => {
    const popoverRect = popoverRef.current?.getBoundingClientRect();
    const markerNode = marker?.element as HTMLElement | null;
    const markerRect = markerNode?.getBoundingClientRect();

    if (popoverRect && markerRect) {
      const mouseY = e.clientY;
      if (mouseY > popoverRect.bottom && mouseY < markerRect.top) {
        return;
      }
    }

    setIsPopoverOpen(false);
    setSelectedProviders([]); // Clear selection on mouse leave
    onMouseOut?.();
  };

  useClickOutside(popoverRef, () => setIsPopoverOpen(false));

  // Escape dismisses the popover (also the keyboard route out of it).
  useEffect(() => {
    if (!isPopoverOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsPopoverOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPopoverOpen]);

  useEffect(() => {
    // Determine if all items are selected based on array length
    if (
      clusterLeaves.length > 0 &&
      selectedProviders.length === clusterLeaves.length
    ) {
      setIsAllSelected(true);
    } else {
      setIsAllSelected(false);
    }
  }, [selectedProviders, clusterLeaves]);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    if (isChecked) {
      // Add all leaf details to the array
      const allProviderDetails = clusterLeaves
        .map((leaf) => leaf?.properties?.details)
        .filter(Boolean);
      setSelectedProviders(allProviderDetails);
    } else {
      // Clear the array
      setSelectedProviders([]);
    }
    // isAllSelected state will update via useEffect
  };

  const handleSelectItem = (
    event: React.ChangeEvent<HTMLInputElement>,
    providerId: string
  ) => {
    const isChecked = event.target.checked;
    const leaf = clusterLeaves.find(
      (l) => l?.properties?.details?.provider_id === providerId
    );
    const providerDetails = leaf?.properties?.details;

    if (!providerDetails) return;

    setSelectedProviders((prev) => {
      if (isChecked) {
        // Add the provider object if checked and not already present
        if (!prev.some((p) => p.provider_id === providerDetails.provider_id)) {
          return [...prev, providerDetails];
        }
        return prev;
      } else {
        // Remove the provider object if unchecked
        return prev.filter(
          (p) => p.provider_id !== providerDetails.provider_id
        );
      }
    });
    // isAllSelected state will update via useEffect
  };

  const handleAddSelectedToList = () => {
    if (!addToList || selectedProviders.length === 0) return;
    addToList(selectedProviders); // Pass the array of selected provider objects
    setIsPopoverOpen(false);
    setSelectedProviders([]); // Clear selection after adding
  };

  const selectedCount = selectedProviders.length; // Count based on array length

  // Highest volume first when the providers carry one; otherwise the given order.
  const sortedLeaves = useMemo(() => {
    const volumeOf = (leaf: any) => leaf?.properties?.details?.volume;
    if (!clusterLeaves.some((leaf) => typeof volumeOf(leaf) === 'number')) {
      return clusterLeaves;
    }
    return [...clusterLeaves].sort(
      (a, b) =>
        (typeof volumeOf(b) === 'number' ? volumeOf(b) : -1) -
        (typeof volumeOf(a) === 'number' ? volumeOf(a) : -1)
    );
  }, [clusterLeaves]);

  const popoverContent = isPopoverOpen && clusterLeaves.length > 0 && (
    <div
      ref={popoverRef}
      onMouseLeave={handlePopoverMouseLeave}
      // The popover is portaled but React still bubbles its clicks to the marker,
      // which would re-run the parent's zoom on every click inside it.
      onClick={(event) => event.stopPropagation()}
      className="fixed w-64 z-[1000] overflow-visible" // Outer div for positioning and connector
      style={{
        top: `${popoverPosition?.top ?? 0}px`,
        left: `${popoverPosition?.left ?? 0}px`,
        opacity: popoverPosition ? 1 : 0,
        transition: 'opacity 0.1s ease-in-out',
      }}
      data-testid={`cluster-popover-${clusterId}`}
    >
      {/* Connector element */}
      <div
        className="absolute h-8 w-8 left-1/2 bottom-0 -mb-8 transform -translate-x-1/2 pointer-events-auto bg-transparent"
        aria-hidden="true"
        data-testid="popover-connector"
      />
      {/* Inner div for styling (rounded corners, background, border, shadow, overflow) */}
      <div className="rounded-lg bg-white shadow-xl border border-neutral-200 flex flex-col overflow-hidden">
        {/* Sticky Header */}
        {addToList && (
          <div className="flex sticky top-0 z-10 items-center justify-between gap-2 border-b border-neutral-200 bg-neutral-50 px-4 py-3">
            <div className="flex gap-2 items-center">
              <Checkbox
                id={`select-all-${clusterId}`}
                checked={isAllSelected}
                onChange={handleSelectAll}
                size="sm"
                color="primary"
                aria-label="Select all items in cluster"
                data-testid={`select-all-checkbox-${clusterId}`}
              />
              <label
                htmlFor={`select-all-${clusterId}`}
                className="flex-1 text-xs font-medium text-neutral-700 cursor-pointer select-none"
              >
                Select all
              </label>
            </div>
            {selectedCount > 0 && (
              <p className="text-xs text-neutral-700">{selectedCount} selected</p>
            )}
          </div>
        )}

        {/* Scrollable Content Area */}
        <div className="overflow-y-auto flex-1 max-h-72">
          {sortedLeaves.map((leaf) => {
            const providerId = leaf?.properties?.details?.provider_id;
            const contentType =
              leaf?.properties?.details?.content_type || leaf?.properties?.details?.type;
            const name = leaf?.properties?.details?.name;
            const specialty = leaf?.properties?.details?.specialty;
            const city = leaf?.properties?.details?.city;
            const state = leaf?.properties?.details?.state;
            const addressLine1 = leaf?.properties?.details?.address_line_1;
            const details = leaf?.properties?.details;

            if (!providerId || !contentType || !name) return null;

            const isSelected = selectedProviders.some(
              (p) => p.provider_id === providerId
            );

            return (
              <div
                key={providerId}
                className="flex items-baseline gap-2 border-b border-neutral-100 px-4 py-2 last:border-b-0 hover:bg-neutral-50"
                data-testid={`cluster-leaf-item-${providerId}`}
              >
                {addToList && (
                  <Checkbox
                    id={`select-item-${providerId}`}
                    checked={isSelected}
                    onChange={(e) => handleSelectItem(e, providerId)}
                    size="sm"
                    color="primary"
                    aria-labelledby={`profile-link-label-${providerId}`}
                    data-testid={`select-item-checkbox-${providerId}`}
                    className="translate-y-0.5"
                  />
                )}
                <div className="flex flex-col flex-1">
                  <ProviderPopupDetails
                    name={name}
                    specialty={specialty}
                    addressLine1={addressLine1}
                    city={city}
                    state={state}
                    onNameClick={() => openProviderProfile(providerId, contentType)}
                    nameId={`profile-link-label-${providerId}`}
                    nameTestId={`profile-link-${providerId}`}
                    metricLabel={details?.volumeName}
                    metrics={getProviderPopupMetrics(details)}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* Sticky Footer */}
        {addToList && (
          <div className="flex sticky bottom-0 z-10 items-center gap-2 border-t border-neutral-200 bg-neutral-50 px-3 py-2">
            <Button
              size="sm"
              onClick={handleAddSelectedToList}
              disabled={selectedCount === 0} // Disable if count is 0
              className={`flex w-full items-center justify-center gap-2 rounded-full px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${
                selectedCount > 0
                  ? 'bg-sky-600 hover:bg-sky-700 focus-visible:outline-sky-600'
                  : 'bg-neutral-300 cursor-not-allowed'
              }`}
              data-testid="add-selected-to-list-button"
            >
              Add To List
            </Button>
          </div>
        )}
      </div>{' '}
      {/* End Inner div */}
    </div> // End Outer div
  );

  return (
    <AdvancedMarker
      ref={markerRef}
      position={position}
      zIndex={size}
      onClick={handleMarkerClick}
      clickable
      className="group relative w-9 h-9 rounded-full cursor-pointer"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <MarkerCircle
        sizeAsText={sizeAsText}
        isHovered={isHovered || isPopoverOpen}
      />

      {isMounted ? createPortal(popoverContent, document.body) : null}
    </AdvancedMarker>
  );
};

export default FeaturesClusterMarker;
